import { useState } from "react";
import { useQuery } from "convex/react";
import { Check, ClipboardList, Layers, Plus } from "lucide-react";
import { api } from "../../../convex/_generated/api";
import { localDayStartMs } from "../../lib/dayHelpers";
import { formatNumber } from "../../lib/format";
import { useLoggingFlow } from "../../lib/logging-flow";
import { buildPlateReviewPayload, usePlate } from "../../lib/plate";

// Temporary Plate bar (plan todo 11). MainAppShell owns the fixed positioning
// wrapper (above the bottom nav, or above the chat composer stack on the chat
// screen) and measures it to pad the scroll content; this component renders
// only the card. Visible across screens whenever the plate has items. The
// plate itself is browser-local only — nothing here writes to the server or
// touches dashboard queries.

export interface PlateBarProps {
  readonly demoMode?: boolean;
}

export function PlateBar({ demoMode = false }: PlateBarProps) {
  const { items, totals } = usePlate();
  const { openLauncher, startFlow } = useLoggingFlow();

  // Stable query args: recomputing Date.now() per render would resubscribe
  // the getToday query on every render.
  const [todayArgs] = useState(() => {
    const now = Date.now();
    return { now, clientDayStart: localDayStartMs(now) };
  });
  const skipQueries = demoMode || items.length === 0;
  // Remaining-targets display: profile daily goals minus today's consumed
  // (same query pair as Today.tsx). Read-only; plate items are never added
  // into any dashboard math.
  const profile = useQuery(api.profile.getProfile, skipQueries ? "skip" : {});
  const today = useQuery(api.dashboard.getToday, skipQueries ? "skip" : todayArgs);

  if (items.length === 0) return null;

  const goalKcal = typeof profile?.dailyGoalKcal === "number" ? profile.dailyGoalKcal : undefined;
  const consumedKcal = typeof today?.consumedKcal === "number" ? today.consumedKcal : undefined;
  const remainingKcal =
    goalKcal !== undefined && consumedKcal !== undefined ? goalKcal - consumedKcal : undefined;
  const afterPlateKcal =
    remainingKcal !== undefined && totals.calories !== undefined
      ? remainingKcal - totals.calories
      : undefined;

  const totalText =
    totals.calories !== undefined
      ? `${formatNumber(Math.round(totals.calories))} kcal`
      : "Some calories unknown";

  const handleAddMore = () => {
    // Opens the Quick Log launcher WITHOUT clearing the plate — new captures
    // append to it (todos 16-18 call usePlate().addItem).
    console.log("[Frontend][Plate] Add more requested; opening launcher", { itemCount: items.length });
    openLauncher();
  };

  const handleReview = (primary: boolean) => {
    console.log("[Frontend][Plate] Review flow started from plate bar", {
      primary,
      itemCount: items.length,
      hasUnknown: totals.hasUnknown,
    });
    startFlow("review", buildPlateReviewPayload(items));
  };

  return (
    <div className="mx-auto max-w-xl px-4 pb-2">
      <div
        data-testid="plate-bar"
        className="pointer-events-auto rounded-2xl border border-border bg-surface px-4 py-3 shadow-[0_-4px_16px_rgba(38,51,44,0.10)]"
      >
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-light text-accent">
                <Layers size={16} strokeWidth={2} aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-text" data-testid="plate-summary">
                  Plate · {items.length} item{items.length === 1 ? "" : "s"}
                </p>
                {remainingKcal !== undefined && (
                  <p className="truncate text-xs text-text-muted" data-testid="plate-remaining">
                    {formatNumber(Math.round(remainingKcal))} kcal left today
                    {afterPlateKcal !== undefined && totals.calories !== undefined && totals.calories > 0
                      ? ` · ${formatNumber(Math.round(afterPlateKcal))} after plate`
                      : ""}
                  </p>
                )}
              </div>
            </div>
            <div className="shrink-0 text-right">
              <p className="text-sm font-semibold text-text" data-testid="plate-total">{totalText}</p>
              {totals.hasUnknown && totals.calories !== undefined && (
                <p className="text-xs text-text-muted">Some calories unknown</p>
              )}
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <button
              type="button"
              data-testid="plate-add-more"
              className="inline-flex min-h-[44px] items-center justify-center gap-1 rounded-xl border border-border bg-surface px-2 text-sm font-semibold text-text transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
              onClick={handleAddMore}
            >
              <Plus size={16} strokeWidth={2} aria-hidden="true" />
              Add more
            </button>
            <button
              type="button"
              data-testid="plate-review"
              className="inline-flex min-h-[44px] items-center justify-center gap-1 rounded-xl border border-border bg-surface px-2 text-sm font-semibold text-text transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
              onClick={() => handleReview(false)}
            >
              <ClipboardList size={16} strokeWidth={2} aria-hidden="true" />
              Review plate
            </button>
            <button
              type="button"
              data-testid="plate-log"
              className="inline-flex min-h-[44px] items-center justify-center gap-1 rounded-xl bg-accent px-2 text-sm font-semibold text-white transition-all duration-200 ease-out active:bg-accent-dark active:scale-[0.98] select-none"
              onClick={() => handleReview(true)}
            >
              <Check size={16} strokeWidth={2} aria-hidden="true" />
              Log meal
            </button>
          </div>
        </div>
    </div>
  );
}
